/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import CONSTANTS from '../../Constants'
import { API_CONFIG, API_LIST } from '../../Constants/ApiDefinations'
import {
  createRoleReqData,
  roleModulesCheckboxKeys,
  roleModulesModal,
  roleStateModal,
} from '../../Models'
import mainApiService from '../../Services'
import { setLoader } from './loader.slice'

const initialState: roleStateModal = {
  roleModules: [] as roleModulesModal[],
  role: null,
  roles: [],
}

const checkAllModules = (indx: number, arr: roleModulesModal[]): boolean => {
  const submodules = arr[indx]?.submodules

  if (!submodules) {
    return false
  }

  return submodules.every((submodule) => {
    const permissions = submodule.permission
    return permissions && Object.values(permissions).every((value) => value === true)
  })
}

function isOdd(number: number) {
  return number % 2 !== 0
}

const roleSlice = createSlice({
  name: 'role',
  initialState,
  reducers: {
    setRoles: (state, action) => {
      state.roles = action.payload.data.records
    },
    setInitialRoles: (state) => {
      state.roleModules = []
      state.role = null
      state.roles = []
    },
    // Make selectAllModule configurable by accepting a config object in action.payload
    selectAllModule: (state, action) => {
      const { idx, type, value } = action.payload
      state.roleModules[idx].selectAll = value
      state.roleModules[idx].submodules = state.roleModules[idx].submodules.map((item) => {
        let permission
        switch (type) {
          // Case for BLITZ_MODULE
          case CONSTANTS.ROLE_EDITOR_MODULE_KEYS.BLITZ_MODULE:
          case CONSTANTS.ROLE_EDITOR_MODULE_KEYS.REPORTS_MODULE:
            permission = { canAccess: value }
            break
          // Cases for META_CATEGORIES, MANAGE_POLICIES, and META_MASTER
          case CONSTANTS.ROLE_EDITOR_MODULE_KEYS.META_CATEGORIES:
          case CONSTANTS.ROLE_EDITOR_MODULE_KEYS.MANAGE_POLICIES:
          case CONSTANTS.ROLE_EDITOR_MODULE_KEYS.META_MASTER:
            permission = { canRead: value, canWrite: value }
            break
          // Case for NOTIFICATION_PREFERENCES
          case CONSTANTS.ROLE_EDITOR_MODULE_KEYS.NOTIF_PREFERENCES_EVENT:
            permission = { enableNotification: value, onScreenNotification: value }
            break
          // Case for TOLLGATES
          case CONSTANTS.ROLE_EDITOR_MODULE_KEYS.TOLLGATES:
            permission = { canSkipValidation: false, canStateTransition: value }
            break

          case CONSTANTS.ROLE_EDITOR_MODULE_KEYS.NOTIF_PREFERENCES_CONTENT:
            permission = { enableNotification: value }
            break
          // Default case to maintain existing permissions
          default:
            permission = { ...item.permission }
        }
        return {
          ...item,
          permission,
        }
      })
    },
    selectHandleModule: (state, action) => {
      const {
        idx,
        subIdx,
        value,
        key,
      }: {
        idx: number
        subIdx: number
        value: boolean
        key: roleModulesCheckboxKeys
      } = action.payload

      // Refactored for clarity and maintainability
      const submodulePermission = state.roleModules[idx].submodules[subIdx].permission
      switch (key) {
        case 'canSkipValidation':
          submodulePermission.canSkipValidation = value
          submodulePermission.canStateTransition = false
          break
        case 'canStateTransition':
          submodulePermission.canSkipValidation = false
          submodulePermission.canStateTransition = value
          break
        default:
          if (
            key === 'enableNotification' &&
            Object.keys(submodulePermission).includes('onScreenNotification') &&
            value === false
          ) {
            submodulePermission.onScreenNotification = false
          }
          submodulePermission[key] = value
      }

      state.roleModules[idx].selectAll = checkAllModules(idx, state.roleModules)
    },
    setRoleModules: (state, action) => {
      if (action?.payload?.data?.length) {
        state.roleModules = action.payload.data.map((item: roleModulesModal) => ({
          ...item,
          selectAll: false,
        }))
      }
      state.roleModules.forEach((permission, index) => {
        if (isOdd(permission.submodules.length)) {
          state.roleModules[index].submodules.push({
            key: '',
            displayName: '',
            permission: { canAccess: false },
          })
        }
      })
    },
    setRole: (state, action) => {
      const { data } = action.payload
      if (!data.error && data?.permissions?.length) {
        state.role = data

        state.roleModules = state.roleModules.map((module, idx) => {
          const matchingPermission = data.permissions.find(
            (permission: roleModulesModal) => permission.key === module.key,
          )

          if (matchingPermission) {
            const updatedSubmodules = module.submodules.map((submodule) => {
              const matchingSubmodule = matchingPermission.submodules.find(
                (sub: any) => sub.key === submodule.key,
              )

              return matchingSubmodule
                ? { ...submodule, permission: matchingSubmodule.permission }
                : submodule
            })

            return {
              ...module,
              submodules: updatedSubmodules,
              selectAll: checkAllModules(idx, data.permissions),
            }
          }

          return module
        })
      }
    },
  },
})

export const {
  selectAllModule,
  selectHandleModule,
  setRole,
  setRoleModules,
  setInitialRoles,
  setRoles,
} = roleSlice.actions

export const createRoleService = createAsyncThunk(
  'role/createRole',
  async (payload: createRoleReqData, { dispatch }) => {
    dispatch(setLoader(true))
    const requestedData = API_CONFIG(API_LIST.ROLES.CREATE_ROLE, payload)
    const data = await mainApiService(requestedData)
    dispatch(setLoader(false))
    return data
  },
)

export const deleteRole = createAsyncThunk('role/deleteRole', async (id: string, { dispatch }) => {
  dispatch(setLoader(true))
  const requestedData = API_CONFIG(API_LIST.ROLES.DELETE_ROLE, undefined, { roleId: id })

  const data = await mainApiService(requestedData)
  dispatch(setLoader(false))

  return data
})

export const viewRoleService = createAsyncThunk(
  'role/viewRole',
  async (id: string, { dispatch }) => {
    dispatch(setLoader(true))
    const requestedData = API_CONFIG(API_LIST.ROLES.VIEW_ROLE, null, { roleId: id })

    const result = await mainApiService(requestedData)
    dispatch(setRole(result))
    dispatch(setLoader(false))
    return result
  },
)

export const getPermissions = createAsyncThunk(
  'role/getPermissions',
  async (payload, { dispatch }) => {
    dispatch(setLoader(true))
    const requestedData = API_CONFIG(API_LIST.PERMISSION_ROLE.GET, {}, null)
    const result = await mainApiService(requestedData)
    dispatch(setRoleModules(result))
    dispatch(setLoader(false))
    return result
  },
)

export const editRoleService = createAsyncThunk(
  'role/editRole',
  async (payload: createRoleReqData, { dispatch }) => {
    dispatch(setLoader(true))
    const requestedData = API_CONFIG(API_LIST.ROLES.EDIT_ROLE, payload, { roleId: payload.roleId })

    const data = await mainApiService(requestedData)
    dispatch(setLoader(false))

    return data
  },
)

export const changeStatus = createAsyncThunk(
  'role/changeStatus',
  async (data: { roleId: string; status: string }, { dispatch }) => {
    dispatch(setLoader(true))
    const requestedData = API_CONFIG(API_LIST.ROLES.CHANGE_STATUS, data, { roleId: data.roleId })

    const result = await mainApiService(requestedData)
    dispatch(setLoader(false))

    return result
  },
)

export const getRolesService = createAsyncThunk('role/getRoles', async (payload, { dispatch }) => {
  dispatch(setLoader(true))
  const requestedData = API_CONFIG(API_LIST.ROLES.LIST_ROLE, null, null, {})
  const result = await mainApiService(requestedData)
  result.data.records.push({
    roleName: 'None',
    _id: 'none',
    status: '',
  })
  dispatch(setRoles(result))
  dispatch(setLoader(false))
})

export type createRoleServiceType = typeof createRoleService

const roleSelector = (state: { role: roleStateModal }) => state.role

export const selectRoleModules = createSelector(roleSelector, (state) => state.roleModules)
export const selectRole = createSelector(roleSelector, (state) => state.role)
export const selectRoles = createSelector(roleSelector, (state) => state.roles)

export default roleSlice
